
import Vue from 'vue';
import Component from 'vue-class-component';
import { namespace } from 'vuex-class';
import { BoPanel } from 'bonomo/dist';
import { IShortCommunity } from '@/model/Community/communityInterfaces';
import { IShortUser } from '@/model/shortInterfaces';
import { ILockerCompany } from '@/model/LockerCompany/lockerCompanyInterface';
import { IZone } from '@/store/modules/settings/zonesModule';
import { IErrors } from '@/model/IErrors';
import { EUserPermissions } from '@/model/EUserPermissions';
import { PermissionHelper } from '@/helpers/PermissionHelper';
import { IAuthUserData } from '@/store/modules/authModule';
import { uiBus } from '@/plugins/uiBus';
import { communityService } from '@/services/communityService';

const lockerModule = namespace('lockerModule');
const authModule = namespace('authModule');
const lockerCompanyModule = namespace('lockerCompanyModule');
const zonesModule = namespace('zonesModule');
const userModule = namespace('userModule');

@Component({
  name: 'LockerCreate',
  components: { BoPanel },
})
export default class LockerCreate extends Vue {

  @authModule.Getter('userData')
  protected authUser!: IAuthUserData;

  @lockerCompanyModule.Getter('lockerCompanies')
  protected lockerCompanies!: ILockerCompany[];

  @lockerCompanyModule.Action('fetchLockerCompanies')
  private fetchLockerCompanies!: (filters?: any) => Promise<any>;

  @zonesModule.Getter('zones')
  protected zones!: IZone[];

  @zonesModule.Action('fetchZones')
  private fetchZones!: () => Promise<any>;

  @userModule.Getter('lockerOwners')
  protected lockerOwners!: IShortUser[];

  @userModule.Action('fetchLockerOwners')
  private fetchLockerOwners!: () => Promise<any>;

  @lockerModule.Action('createLocker')
  private createLocker!: (data: any) => Promise<any>;

  @lockerModule.Action('fetchLockerThemes')
  private fetchLockerThemes!: () => Promise<string[]>;

  protected loading: boolean = false;
  protected saving: boolean = false;
  private allCommunities: IShortCommunity[] = [];
  private communitiesLoading: boolean = false;
  private themes: string[] = [];
  private themesLoading: boolean = false;
  private errors: IErrors = {};

  readonly availableLanguages = ['es', 'ca', 'en', 'fr', 'pt', 'dk', 'de'];

  private form: any = {
    name: '',
    abbreviation: '',
    description: '',
    location_description: '',
    location_building_name: '',
    location_building_floor: '',
    default_phone_prefix: '',
    should_send_reminders: false,
    languages: ['es'],
    community_id: null,
    channel_id: 1,
    owner_user_id: null,
    zone_id: null,
    status: 'Install',
    internal_delivery_type: 0,
    theme_name: null,
    lockerCompanyIds: [],
  };

  private rules = {
    required: (v: any) => (v !== null && v !== undefined && v !== '') || this.$t('common.validation.required'),
    min2: (v: string) => (!!v && v.length >= 2) || this.$t('common.validation.min', { min: 2 }),
  };

  get isAdmin(): boolean {
    return !!this.authUser && PermissionHelper.hasPermissionAny(this.authUser, EUserPermissions.PERMISSION_ADMINISTRATOR);
  }

  get channelItems(): { text: string; value: number }[] {
    const channels = this.$t('channels.names') as any;
    const ret: { text: string; value: number }[] = [];
    const keys = Object.keys(channels);
    for (let idx = 0; idx < keys.length; idx++) {
      const key = keys[idx];
      const num = Number(key);
      if (!isNaN(num)) {
        ret.push({ text: channels[key], value: num });
      }
    }
    return ret;
  }

  get themeItems(): string[] {
    return this.themes;
  }

  get communityItems(): { text: string; value: number }[] {
    return this.allCommunities.map((c) => ({ text: c.name, value: c.id }));
  }

  get lockerCompanyItems(): { text: string; value: number }[] {
    return (this.lockerCompanies || []).map(c => ({ text: c.description, value: c.id }));
  }

  get zoneItems(): { text: string; value: number }[] {
    return (this.zones || []).map(z => ({ text: z.name, value: z.id }));
  }

  get statusOptions() {
    return [
      { text: 'Install', value: 'Install' },
      { text: 'Online', value: 'Online' },
      { text: 'Off', value: 'Off' },
      { text: 'Deleted', value: 'Deleted' },
    ];
  }

  get internalDeliveryOptions() {
    return [
      { text: 'Off', value: 0 },
      { text: 'Bayer', value: 1 },
    ];
  }

  loadData() {
    this.fetchAllCommunitiesLocal();
    this.fetchThemesLocal();
    if (!this.lockerCompanies || !this.lockerCompanies.length) {
      this.fetchLockerCompanies();
    }
    if (!this.zones || !this.zones.length) {
      this.fetchZones();
    }
    if (!this.lockerOwners || !this.lockerOwners.length) {
      this.fetchLockerOwners();
    }
  }

  private async fetchThemesLocal() {
    this.themesLoading = true;
    try {
      this.themes = await this.fetchLockerThemes();
    } catch (e) {
      // silenced: theme dropdown will be empty
    } finally {
      this.themesLoading = false;
    }
  }

  private async fetchAllCommunitiesLocal() {
    this.communitiesLoading = true;
    try {
      this.allCommunities = await communityService.fetchAll();
    } catch (e) {
      // silenced: community dropdown will be empty
    } finally {
      this.communitiesLoading = false;
    }
  }

  ownerLabel(user: IShortUser): string {
    return user.first_name + ' ' + user.last_name + ' <' + user.email + '>';
  }

  private async onSave() {
    const formRef = this.$refs.createForm as any;
    if (formRef && !formRef.validate()) return;

    this.saving = true;
    this.errors = {};
    try {
      const lockerCompanyIds = this.form.lockerCompanyIds;
      const lockerData: any = {};
      for (const key in this.form) {
        if (key !== 'lockerCompanyIds') {
          lockerData[key] = this.form[key];
        }
      }
      lockerData.companies = lockerCompanyIds || [];

      const createdLocker = await this.createLocker(lockerData);
      uiBus.emit('success', this.$t('common.saved'));
      this.$router.push({ name: 'locker', params: { lockerId: '' + createdLocker.id } });
    } catch (e) {
      const err = e as any;
      if (err && err.response && err.response.data && err.response.data.errors) {
        const errs: any = {};
        err.response.data.errors.forEach((apiErr: any) => {
          const key = apiErr.source && apiErr.source.pointer ? apiErr.source.pointer : '_';
          errs[key] = errs[key] || [];
          errs[key].push(apiErr.title);
        });
        this.errors = errs;
      }
    } finally {
      this.saving = false;
    }
  }

  private onCancel() {
    this.$router.push({ name: 'installList' });
  }
}
